// document.store.ts
import { inject } from '@angular/core';
import {
  patchState,
  signalStore,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import {
  addEntities,
  SelectEntityId,
  setAllEntities,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { exhaustMap, map, pipe, switchMap, tap } from 'rxjs';
import {
  FileUploadResponseDto,
  FileUploadService,
} from '../../../core/services/file-upload.service';
import { DocumentService } from '../../../core/services/document/document.service';
import { DataStatus, ProviderDocument, StoreError } from '../models/models';

export interface FileUploadToS3 {
  presigned_url: string;
  object_key: string;
  bucket_name: string;
  file: File;
}

interface DocumentState {
  documentStatus: DataStatus;
  uploadDocumentStatus: DataStatus;
  activeClinicId: number;
  error: StoreError;
  pendingUploadTitle: string;
  pendingUploadDescription: string;
  pendingUploadDocumentTypeId: number;
}

const getDocumentId = (doc: any): number => Number(doc?.document_id ?? doc?.id ?? 0);

const selectDocumentId: SelectEntityId<ProviderDocument> = (doc: ProviderDocument) => getDocumentId(doc);

const extractDocuments = (response: any): any[] => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.documents)) {
    return response.documents;
  }

  if (Array.isArray(response?.result)) {
    return response.result;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
};

const extractDocument = (response: any): any => {
  if (response?.document) {
    return response.document;
  }

  if (response?.result && !Array.isArray(response.result)) {
    return response.result;
  }

  if (response?.data && !Array.isArray(response.data)) {
    return response.data;
  }

  return response ?? {};
};

const normalizeProviderDocument = (document: any): ProviderDocument => {
  const documentId = getDocumentId(document);

  return {
    ...document,
    id: document.id ?? documentId,
    document_id: documentId,
    provider_id: Number(document.provider_id ?? 0),
    document_type_id: Number(document.document_type_id ?? 0),
    title: document.title ?? document.document_type_name ?? null,
    presigned_url: document.presigned_url ?? document.document_link ?? null,
    document_link: document.document_link ?? document.presigned_url ?? null,
    active: document.active !== false,
  } as unknown as ProviderDocument;
};

export const DocumentStore = signalStore(
  { providedIn: 'root', protectedState: false },

  withState<DocumentState>({
    documentStatus: 'idle',
    uploadDocumentStatus: 'idle',
    activeClinicId: 0,
    pendingUploadTitle: '',
    pendingUploadDescription: '',
    pendingUploadDocumentTypeId: 0,
    error: { error: false, message: '' },
  }),

  withEntities<ProviderDocument>(),

  withProps(() => ({
    documentService: inject(DocumentService),
    fileUploadService: inject(FileUploadService),
  })),

  withMethods((store) => ({
    setDocumentStatus(documentStatus: DataStatus) {
      patchState(store, { documentStatus });
    },

    setUploadDocumentStatus(uploadDocumentStatus: DataStatus) {
      patchState(store, { uploadDocumentStatus });
    },

    setError(message: string) {
      patchState(store, {
        documentStatus: 'error',
        error: { error: true, message },
      });
    },

    clearError() {
      patchState(store, {
        error: { error: false, message: '' },
      });
    },

    setActiveClinicId(activeClinicId: number) {
      patchState(store, { activeClinicId });
    },

    getDocumentsByProviderId(provider_id: number) {
      const providerId = Number(provider_id);

      return store.entities().filter((document: any) =>
        Number(document.provider_id) === providerId &&
        document.active !== false
      );
    },

    setPendingUploadTitle(pendingUploadTitle: string) {
      patchState(store, { pendingUploadTitle });
    },

    setPendingUploadDescription(pendingUploadDescription: string) {
      patchState(store, { pendingUploadDescription });
    },

    setPendingUploadDocumentTypeId(pendingUploadDocumentTypeId: number) {
      patchState(store, { pendingUploadDocumentTypeId });
    }
  })),

  withMethods((store) => ({
    loadDocumentsByClinic: rxMethod<number>(
      pipe(
        tap(() => {
          store.clearError();
          store.setDocumentStatus('loading');
        }),
        switchMap((clinic_id) =>
          store.documentService.getAllDocumentsByClinic(clinic_id).pipe(
            tapResponse({
              next: (response: any) => {
                const documents = extractDocuments(response).map(normalizeProviderDocument);

                patchState(store, setAllEntities(documents, {
                  selectId: selectDocumentId,
                }));

                store.setDocumentStatus('success');
              },
              error: (err: unknown) => {
                console.error(err);
                store.setError('Failed to load documents.');
              },
            })
          )
        )
      )
    ),

    editAdminDocument: rxMethod<{ dto: any; onSuccess?: () => void }>(
      pipe(
        tap(() => {
          store.clearError();
          store.setDocumentStatus('loading');
        }),
        exhaustMap(({ dto, onSuccess }) =>
          store.documentService.editAdminDocument(dto).pipe(
            tapResponse({
              next: (response: any) => {
                const documentId = Number(dto.id);
                const existingDocument = store.entities().find((document: any) => getDocumentId(document) === documentId) ?? {};
                const responseDocument = extractDocument(response);
                const document = normalizeProviderDocument({
                  ...existingDocument,
                  ...responseDocument,
                  id: responseDocument?.id ?? responseDocument?.document_id ?? documentId,
                  document_id: responseDocument?.document_id ?? responseDocument?.id ?? documentId,
                  document_type_id: dto.document_type_id ?? responseDocument?.document_type_id ?? (existingDocument as any).document_type_id,
                  document_type_name: dto.document_type_name ?? responseDocument?.document_type_name ?? (existingDocument as any).document_type_name,
                  title: dto.title ?? responseDocument?.title ?? responseDocument?.document_type_name ?? (existingDocument as any).title,
                });

                patchState(store, updateEntity({
                  id: documentId,
                  changes: document,
                }, {
                  selectId: selectDocumentId,
                }));

                store.setDocumentStatus('success');
                onSuccess?.();
              },
              error: () => {
                store.setDocumentStatus('error');
              }
            })
          )
        )
      )
    ),

    deleteAdminDocument: rxMethod<{ document_id: number; onSuccess?: () => void }>(
      pipe(
        tap(() => {
          store.clearError();
          store.setDocumentStatus('loading');
        }),
        exhaustMap(({ document_id, onSuccess }) =>
          store.documentService.deleteAdminDocument(document_id).pipe(
            tapResponse({
              next: () => {
                const documents = store.entities().filter((document: any) => getDocumentId(document) !== Number(document_id));

                patchState(store, setAllEntities(documents, {
                  selectId: selectDocumentId,
                }));

                store.setDocumentStatus('success');
                onSuccess?.();
              },
              error: () => {
                store.setDocumentStatus('error');
              }
            })
          )
        )
      )
    ),

    uploadFile: rxMethod<{
      dto: any;
      file: File;
    }>(
      pipe(
        tap(() => {
          store.clearError();
          store.setUploadDocumentStatus('loading');
        }),
        switchMap(({ dto, file }) =>
          store.fileUploadService.preFlightCommunicationFileUpload(dto).pipe(
            switchMap((response: FileUploadResponseDto) => {
              const {
                presigned_s3_link,
                object_key,
                bucket_name,
                content_type,
              } = response;

              return store.fileUploadService
                .uploadFileToS3(presigned_s3_link, content_type, file)
                .pipe(
                  switchMap(() =>
                    store.documentService.saveFile({
                      id: dto.id,
                      document_id: dto.document_id,
                      object_key,
                      bucket_key: bucket_name,
                      provider_id: dto.provider_id,
                      document_type_id: dto.document_type_id,
                      document_type_name: dto.document_type_name,
                      title: dto.title,
                      licence_expire_date: dto.licence_expire_date ?? dto.license_expire_date ?? null,
                      license_expire_date: dto.license_expire_date ?? dto.licence_expire_date ?? null,
                      insurance_expire_date: dto.insurance_expire_date ?? null,
                      active: true,
                    })
                  ),
                  map((saved: any) => normalizeProviderDocument({
                    ...(store.entities().find((document: any) => getDocumentId(document) === Number(dto.document_id ?? dto.id)) ?? {}),
                    ...saved,
                    id: dto.id ?? saved?.id,
                    document_id: dto.document_id ?? saved?.document_id ?? saved?.id,
                    object_key,
                    bucket_key: bucket_name,
                    provider_id: dto.provider_id,
                    document_type_id: dto.document_type_id,
                    document_type_name: dto.document_type_name ?? saved?.document_type_name,
                    title: dto.title ?? saved?.title ?? saved?.document_type_name,
                    licence_expire_date: dto.licence_expire_date ?? dto.license_expire_date ?? saved?.licence_expire_date ?? null,
                    license_expire_date: dto.license_expire_date ?? dto.licence_expire_date ?? saved?.license_expire_date ?? null,
                    insurance_expire_date: dto.insurance_expire_date ?? saved?.insurance_expire_date ?? null,
                    active: true,
                  }))
                );
            }),
            tapResponse({
              next: (document: ProviderDocument) => {
                const replacingDocumentId = Number(dto.document_id ?? dto.id ?? 0);

                if (replacingDocumentId) {
                  patchState(store, updateEntity({
                    id: replacingDocumentId,
                    changes: document,
                  }, {
                    selectId: selectDocumentId,
                  }));
                } else {
                  patchState(store, addEntities([document], {
                    selectId: selectDocumentId,
                  }));
                }

                store.setUploadDocumentStatus('success');
              },
              error: (err: unknown) => {
                console.error(err);
                store.setUploadDocumentStatus('error');
                store.setError('Failed to upload document.');
              },
            })
          )
        )
      )
    ),
  })),

  withMethods((store) => ({
    completeFileUpload: rxMethod<{ object_key: string; bucket_name: string }>(
      pipe(
        exhaustMap(({ object_key, bucket_name }) =>
          store.documentService.uploadDocumentCenterDetails({
            object_key,
            bucket_name,
            title: store.pendingUploadTitle(),
            description: store.pendingUploadDescription(),
            document_type_id: store.pendingUploadDocumentTypeId(),
          }).pipe(
            tapResponse({
              next: (_response: unknown) => {},
              error: (_err: unknown) => {
                store.setError('Failed to upload document to s3. Please contact helpdesk');
                store.setDocumentStatus('error');
              }
            })
          )
        )
      )
    )
  })),

  withMethods((store) => ({
    uploadFileToS3: rxMethod<FileUploadToS3>(
      pipe(
        tap(() => {
          store.clearError();
        }),
        exhaustMap(({ presigned_url, object_key, bucket_name, file }) =>
          store.documentService.uploadFileToS3(presigned_url, file).pipe(
            tapResponse({
              next: (_response: unknown) => {
                store.completeFileUpload({ bucket_name, object_key });
              },
              error: (_err: unknown) => {
                store.setError('Failed to upload document to s3. Please contact helpdesk');
                store.setDocumentStatus('error');
              }
            })
          )
        )
      )
    )
  })),

  withMethods((store) => ({
    getDocumentCenterUploadUrl: rxMethod<File>(
      pipe(
        tap(() => {
          store.setDocumentStatus('loading');
        }),
        exhaustMap((file) =>
          store.documentService.getDocumentCenterUploadUrl(file.name).pipe(
            tapResponse({
              next: (response: any) => {
                const { presigned_url, object_key, bucket_name, content_type } = response;
                store.uploadFileToS3({ presigned_url, object_key, bucket_name, file });
              },
              error: (err: unknown) => {
                console.error(err);
                store.setError('Failed to upload document.');
              }
            })
          )
        )
      )
    )
  }))
);
